import React, { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { ArrowDownLeft, ArrowUpRight, MessageSquare } from 'lucide-react';
import { api } from '../lib/api';
import { useApps } from '../lib/hooks';
import { getAppBadge, AppBadge, AppInfo } from '../lib/appBadge';

interface Message {
  id: string;
  direction: 'inbound' | 'outbound';
  content: string;
  response?: string;
  timestamp: string;
  tokensUsed?: number;
}

interface AppsResponse {
  serviceNumber?: string;
  apps?: AppInfo[];
}

const formatTimestamp = (timestamp: string): string => {
  try {
    const date = new Date(timestamp);
    return isNaN(date.getTime()) ? 'Invalid date' : date.toLocaleString();
  } catch {
    return 'Invalid date';
  }
};

const chipClass = (active: boolean) =>
  `px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
    active
      ? 'bg-gray-900 text-white border-gray-900'
      : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
  }`;

const MessageHistory: React.FC = () => {
  const { data: messages, isLoading } = useQuery<Message[]>('history', async () => {
    const { data } = await api.get('/api/history');
    return data.messages || [];
  });
  const { data: appsData } = useApps() as { data?: AppsResponse };
  const apps = useMemo(() => appsData?.apps || [], [appsData]);
  const [filter, setFilter] = useState<string>('All');

  const messageList = useMemo(() => messages || [], [messages]);

  const badgeFor = React.useCallback(
    (message: Message) => getAppBadge(message.content, apps),
    [apps]
  );

  const keywordsWithMatches = useMemo(() => {
    const seen = new Set<string>();
    const ordered: string[] = [];
    messageList.forEach(message => {
      const badge = badgeFor(message);
      if (badge && !seen.has(badge)) {
        seen.add(badge);
        ordered.push(badge);
      }
    });
    return ordered;
  }, [messageList, badgeFor]);

  const filteredMessages =
    filter === 'All' ? messageList : messageList.filter(message => badgeFor(message) === filter);

  if (isLoading) {
    return (
      <div className="flex justify-center mt-8">
        <div className="w-8 h-8 border-2 border-gray-300 border-t-gray-900 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Message History</h2>

      <div className="flex flex-wrap gap-2 mb-6">
        <button
          type="button"
          onClick={() => setFilter('All')}
          className={chipClass(filter === 'All')}
        >
          All
        </button>
        {keywordsWithMatches.map(keyword => (
          <button
            key={keyword}
            type="button"
            onClick={() => setFilter(keyword)}
            className={chipClass(filter === keyword)}
          >
            {keyword}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-lg border border-gray-200">
        {filteredMessages.length > 0 ? (
          <div className="divide-y divide-gray-200">
            {filteredMessages.map(message => {
              const badge = badgeFor(message);
              return (
                <div key={message.id} className="px-6 py-4">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center">
                      {message.direction === 'inbound' ? (
                        <ArrowDownLeft className="w-4 h-4 text-green-500" aria-hidden="true" />
                      ) : (
                        <ArrowUpRight className="w-4 h-4 text-blue-500" aria-hidden="true" />
                      )}
                      <span className="ml-2 text-sm font-medium text-gray-900">
                        {message.direction === 'inbound' ? 'Received' : 'Sent'}
                      </span>
                      {badge && <AppBadge label={badge} />}
                    </div>
                    <span className="text-xs text-gray-500">{formatTimestamp(message.timestamp)}</span>
                  </div>
                  <p className="text-sm text-gray-900">{message.content}</p>
                  {message.response && (
                    <p className="text-sm text-gray-500 mt-1">{message.response}</p>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="px-6 py-12 text-center">
            <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-sm font-medium text-gray-700">No messages match this filter</p>
            <p className="text-xs text-gray-500 mt-1">
              Start by sending a text to our SMS number.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MessageHistory;
